import React, { useState, useRef, useEffect } from 'react';
import { Card, Form, Button, ListGroup, Badge, Spinner, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { faceImageAPI } from '../utils/api';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

// Props for the AI chat assistant component
interface ReportChatAssistantProps {
  reportData: any[];
  viewMode: 'individual' | 'summary';
  isLoading?: boolean;
  totalRecords?: number;
  currentFilters?: any;
  nurseryId?: string;
}

const ReportChatAssistant: React.FC<ReportChatAssistantProps> = ({ 
  reportData, 
  viewMode,
  isLoading = false,
  totalRecords = 0,
  currentFilters = {},
  nurseryId = ''
}) => {
  const { t } = useTranslation('components', { keyPrefix: 'reportChatAssistant' });
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [fullData, setFullData] = useState<any[]>([]);
  const [isLoadingFullData, setIsLoadingFullData] = useState(false);
  const [hasLoadedFullData, setHasLoadedFullData] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatBodyRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    // Scroll within the chat container only, not the entire page
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch all data when chat is expanded for individual view
  useEffect(() => {
    const needsFullData = 
      isExpanded && 
      viewMode === 'individual' && 
      !hasLoadedFullData && 
      !isLoadingFullData &&
      totalRecords > reportData.length &&
      nurseryId;

    if (needsFullData) {
      loadAllData();
    }
  }, [isExpanded, viewMode, hasLoadedFullData, isLoadingFullData, totalRecords, reportData.length, nurseryId]);

  const loadAllData = async () => {
    setIsLoadingFullData(true);
    setError(null);

    try {
      const maxBatch = 2000;
      const limitedTotal = Math.min(totalRecords, maxBatch);
      
      console.log(`📥 Fetching ${limitedTotal} of ${totalRecords} records for AI analysis...`);
      
      const response = await faceImageAPI.getFaceImages(nurseryId!, {
        ...currentFilters,
        per_page: limitedTotal,
        page: 1
      });

      if (response.success && response.data) {
        setFullData(response.data);
        setHasLoadedFullData(true);
        console.log(`✅ Loaded ${response.data.length} records for AI analysis`);
        
        if (response.data.length < totalRecords) {
          console.log(`ℹ️ Note: Analyzing first ${response.data.length} of ${totalRecords} records`);
        }
      } else {
        throw new Error('API returned unsuccessful response');
      }
    } catch (err: any) {
      console.error('Error loading full data:', err);
      
      const isTimeout = err.code === 'ECONNABORTED' || err.message?.includes('timeout');
      
      if (isTimeout) {
        console.log('⚠️ Request timeout. Trying with 500 records...');
        try {
          const mediumBatch = 500;
          const response = await faceImageAPI.getFaceImages(nurseryId!, {
            ...currentFilters,
            per_page: mediumBatch,
            page: 1
          });
          
          if (response.success && response.data) {
            setFullData(response.data);
            setHasLoadedFullData(true);
            console.log(`✅ Loaded ${response.data.length} records (reduced batch due to timeout)`);
            return;
          }
        } catch (mediumErr) {
          console.error('Medium batch (500) also failed, trying smaller batch...');
          
          try {
            const smallBatch = 200;
            const response = await faceImageAPI.getFaceImages(nurseryId!, {
              ...currentFilters,
              per_page: smallBatch,
              page: 1
            });
            
            if (response.success && response.data) {
              setFullData(response.data);
              setHasLoadedFullData(true);
              console.log(`✅ Loaded ${response.data.length} records (small batch)`);
              return;
            }
          } catch (smallErr) {
            console.error('Small batch (200) also failed:', smallErr);
          }
        }
      }
      
      console.log('⚠️ Using current page data as fallback');
      setFullData(reportData);
      setHasLoadedFullData(true);
    } finally {
      setIsLoadingFullData(false);
    }
  };

  // Suggested questions based on view mode
  const suggestedQuestions = viewMode === 'summary' 
    ? [
      t('whoMostCheckIns'),
        t('whoHadEarlyDropoff'),
        t('attendancePatterns'),
        t('irregularAttendance')
      ]
    : [
        t('mostCheckInTimes'),
        t('pickupPatterns'),
        t('classroomMostActivity'),
        t('summarizeToday')
      ];

  const askChatGPT = async (question: string) => {
    const dataToAnalyze = viewMode === 'summary' 
      ? reportData 
      : (hasLoadedFullData && fullData.length > 0 ? fullData : reportData);

    if (!question.trim() || dataToAnalyze.length === 0) return;

    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    console.log('🔑 API Key loaded:', apiKey ? `${apiKey.substring(0, 20)}... ✅` : '❌ UNDEFINED');
    console.log(`📊 Analyzing ${dataToAnalyze.length} ${viewMode === 'summary' ? 'children' : 'records'}`);
    
    if (!apiKey) {
      setError(t('apiKeyMissing'));
      return;
    }

    setIsProcessing(true);
    setError(null);

    const userMessage: Message = {
      role: 'user',
      content: question,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');

    try {
      const limitedData = dataToAnalyze.slice(0, 100);
      const totalAvailable = dataToAnalyze.length;
      const dataContext = viewMode === 'summary'
        ? `Child Summary Data (${totalAvailable} children total, analyzing first ${limitedData.length}):\n${JSON.stringify(limitedData, null, 2)}`
        : `Individual Check-in Records (${totalAvailable} records total, analyzing first ${limitedData.length}):\n${JSON.stringify(limitedData, null, 2)}`;

      const systemPrompt = `You are an attendance data analyst assistant for LittleLens. Analyze the provided attendance data and answer questions concisely and accurately.

Data format:
- ${viewMode === 'summary' ? 'Child summary with total detections, drop-off/pick-up times, classrooms' : 'Individual detection records with timestamps, child names, camera info'}

Instructions:
- Be specific with numbers and names
- Format responses clearly with bullet points when listing multiple items
- Use the nursery schedule drop-off and pick-up times when the data contains them; otherwise avoid guessing punctuality rules
- If the data doesn't contain enough information to answer, say so clearly
- Keep responses concise but informative
- Use emojis sparingly for better readability
- Focus on the most relevant insights`;

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            {
              role: 'system',
              content: systemPrompt
            },
            {
              role: 'user',
              content: `${dataContext}\n\nQuestion: ${question}`
            }
          ],
          temperature: 0.3,
          max_tokens: 500
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `API request failed: ${response.statusText}`);
      }

      const data = await response.json();
      const answer = data.choices[0].message.content;

      const assistantMessage: Message = {
        role: 'assistant',
        content: answer,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);

    } catch (err: any) {
      console.error('Chat error:', err);
      setError(err.message || t('responseFailed'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim() && !isProcessing) {
      askChatGPT(input);
    }
  };

  const handleSuggestedQuestion = (question: string) => {
    setInput(question);
    askChatGPT(question);
  };

  const clearChat = () => {
    setMessages([]);
    setError(null);
  };

  if (isLoading) {
    return (
      <Card className="mb-4 shadow-sm">
        <Card.Body className="text-center py-4">
          <Spinner animation="border" size="sm" className="me-2" />
          <span className="text-muted">{t('loadDataFirst')}</span>
        </Card.Body>
      </Card>
    );
  }

  if (reportData.length === 0) {
    return (
      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-info bg-opacity-10 border-info">
          <div className="d-flex align-items-center">
            <span className="me-2" style={{ fontSize: '1.2rem' }}>💬</span>
            <strong className="text-info">{t('aiReady')}</strong>
          </div>
        </Card.Header>
        <Card.Body>
          <Alert variant="info" className="mb-0">
            <strong>💡 {t('howToUse')}</strong>
            <ol className="mb-0 mt-2 ps-3">
              <li>{t('stepApplyFilters')}</li>
              <li>{t('stepAskQuestions')}</li>
              <li>{t('stepGetInsights')}</li>
            </ol>
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  const displayCount = viewMode === 'summary' 
    ? reportData.length 
    : (hasLoadedFullData && fullData.length > 0 ? fullData.length : totalRecords || reportData.length);

  return (
    <Card className="mb-4 shadow-sm border-primary">
      <Card.Header 
        className="bg-primary text-white d-flex justify-content-between align-items-center"
        style={{ cursor: isExpanded ? 'default' : 'pointer' }}
        onClick={() => !isExpanded && setIsExpanded(true)}
      >
        <div>
          <span className="me-2" style={{ fontSize: '1.2rem' }}>🤖</span>
          <strong>{t('headerTitle')}</strong>
          <Badge bg="light" text="dark" className="ms-2">
            {displayCount} {viewMode === 'summary' ? t('children') : t('records')}
          </Badge>
          {isLoadingFullData && (
            <Spinner animation="border" size="sm" className="ms-2" />
          )}
        </div>
        <div className="d-flex gap-2">
          {messages.length > 0 && (
            <Button 
              variant="outline-light" 
              size="sm" 
              onClick={(e) => {
                e.stopPropagation();
                clearChat();
              }}
              title={t('clearConversation')}
            >
              🗑️
            </Button>
          )}
          {isExpanded && (
            <Button 
              variant="outline-light" 
              size="sm" 
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(false);
              }}
              title={t('minimize')}
            >
              ▼
            </Button>
          )}
        </div>
      </Card.Header>
      
      {isExpanded && (
        <>
          <Card.Body ref={chatBodyRef} style={{ maxHeight: '400px', overflowY: 'auto', minHeight: '200px' }}>
            {isLoadingFullData ? (
              <div className="text-center py-5">
                <Spinner animation="border" role="status" className="mb-3" />
                <p className="text-muted">{t('loadingRecords', { totalRecords })}</p>
                <small className="text-muted">{t('mayTakeSeconds')}</small>
              </div>
            ) : messages.length === 0 ? (
              <div>
                <p className="text-muted mb-3">
                  <strong>💬 {t('startConversation')}</strong> {t('tryThese')}
                </p>
                <div className="d-flex flex-wrap gap-2">
                  {suggestedQuestions.map((question, idx) => (
                    <Badge 
                      key={idx}
                      bg="light" 
                      text="primary" 
                      className="p-2"
                      style={{ cursor: 'pointer', fontSize: '0.85rem', fontWeight: 'normal' }}
                      onClick={() => handleSuggestedQuestion(question)}
                    >
                      {question}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : (
              <ListGroup variant="flush">
                {messages.map((msg, idx) => (
                  <ListGroup.Item 
                    key={idx} 
                    className={`border-0 ${msg.role === 'user' ? 'text-end' : ''}`}
                  >
                    <div 
                      className={`d-inline-block ${msg.role === 'user' ? 'bg-primary text-white' : 'bg-light'} p-3 rounded`} 
                      style={{ maxWidth: '80%', textAlign: 'left' }}
                    >
                      <div className="d-flex align-items-start mb-1">
                        <span className="me-2" style={{ fontSize: '1rem' }}>
                          {msg.role === 'assistant' ? '🤖' : '👤'}
                        </span>
                        <small className="opacity-75">
                          {msg.timestamp.toLocaleTimeString()}
                        </small>
                      </div>
                      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {msg.content}
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
                {isProcessing && (
                  <ListGroup.Item className="border-0">
                    <div className="d-inline-block bg-light p-3 rounded">
                      <Spinner animation="border" size="sm" className="me-2" />
                      <span>{t('analyzingData')}</span>
                    </div>
                  </ListGroup.Item>
                )}
                <div ref={messagesEndRef} />
              </ListGroup>
            )}

            {error && (
              <Alert variant="danger" className="mt-3 mb-0" dismissible onClose={() => setError(null)}>
                <strong>{t('error')}</strong> {error}
              </Alert>
            )}
          </Card.Body>

          <Card.Footer className="bg-light">
            <Form onSubmit={handleSubmit}>
              <div className="d-flex gap-2">
                <Form.Control
                  type="text"
                  placeholder={t('inputPlaceholder')}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  disabled={isProcessing}
                  autoFocus
                />
                <Button 
                  type="submit" 
                  variant="primary" 
                  disabled={isProcessing || !input.trim()}
                  style={{ minWidth: '45px' }}
                >
                  ➤
                </Button>
              </div>
            </Form>
            <small className="text-muted d-block mt-2">
              💡 {t('tip')}
            </small>
          </Card.Footer>
        </>
      )}
      
      {!isExpanded && (
        <Card.Body className="py-2 text-center" style={{ cursor: 'pointer' }} onClick={() => setIsExpanded(true)}>
          <small className="text-muted">
            <span className="me-1">💬</span>
            {t('clickToStart')}
          </small>
        </Card.Body>
      )}
    </Card>
  );
};

export default ReportChatAssistant;
